"use client";

import { useEffect, useState } from "react";
import ProductCard from "./ProductCard";
import QuickAddPanel from "./QuickAddPanel";
import type { CatalogLite } from "../../lib/types";

/**
 * The catalog grid, and the owner of the inline quick-add panel.
 *
 * The panel is injected after the LAST tile in the open tile's row, so it
 * spans the full width without leaving a hole in the row it belongs to — the
 * same placement rule as the Glaza God storefront, done from the column count
 * rather than by measuring offsets. The grid is `grid-cols-2 md:grid-cols-4`,
 * so the column count is known from one media query.
 */
const MD = "(min-width: 768px)";

function useColumnCount(): number {
  // Starts at the desktop count so the first paint after hydration matches the
  // common case; the panel only ever renders after a click, so nothing about
  // this reaches the server HTML.
  const [cols, setCols] = useState(4);
  useEffect(() => {
    const mq = window.matchMedia(MD);
    const apply = () => setCols(mq.matches ? 4 : 2);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);
  return cols;
}

export default function ProductGrid({
  products,
  className = "",
  eagerCount = 2,
}: {
  products: CatalogLite[];
  className?: string;
  /** How many leading cards load eagerly at high priority (first row on phones). */
  eagerCount?: number;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const cols = useColumnCount();
  // The first N cards that actually have a photo (an image-less card can't be
  // an LCP candidate, so it shouldn't use up one of the eager slots).
  const eagerIds = new Set(products.filter((p) => p.image).slice(0, eagerCount).map((p) => p.id));

  const openIndex = openId ? products.findIndex((p) => p.id === openId) : -1;
  const openProduct = openIndex >= 0 ? products[openIndex] : null;
  // Last tile of the row the open tile sits in, clamped to the final tile.
  const panelAfterIndex =
    openIndex >= 0
      ? Math.min(Math.floor(openIndex / cols) * cols + cols - 1, products.length - 1)
      : -1;

  return (
    <div className={`grid grid-cols-2 gap-x-5 gap-y-10 md:grid-cols-4 ${className}`}>
      {products.flatMap((p, i) => {
        const tile = (
          <ProductCard
            key={p.id}
            product={p}
            priority={eagerIds.has(p.id)}
            isOpen={openId === p.id}
            onToggleQuickAdd={() => setOpenId((cur) => (cur === p.id ? null : p.id))}
          />
        );
        if (!openProduct || i !== panelAfterIndex) return [tile];
        return [
          tile,
          // Keyed by id so switching between two products remounts the panel
          // and resets the size selection instead of carrying the old choice.
          <QuickAddPanel
            key={`panel-${openProduct.id}`}
            product={openProduct}
            onClose={() => setOpenId(null)}
          />,
        ];
      })}
    </div>
  );
}
